import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useDispatch, useSelector} from 'react-redux';
import {
  StyledBudgetActiveInfo,
  StyledBudgetActiveInfoEmpty,
  StyledBudgetActiveInfoPanel,
  StyledBudgetActiveInfoPanelAmount,
  StyledBudgetActiveInfoPanelDot,
  StyledBudgetActiveInfoPanelHeader,
  StyledBudgetActiveInfoPanelHeaderTitle,
  StyledBudgetActiveInfoPanelPercentage,
  StyledBudgetActiveInfoPanelTitle,
} from './StyledBudgetActiveInfo';
import BudgetInvoicesTable from '../BudgetInvoicesTable';
import OverflowProgress from '../../../OverflowProgress';
// import BudgetTransactionsTable from '../BudgetTransactionsTable';
import LoadMoreButton from '../../TransactionsPage/LoadMoreButton';
import {
  amountHelpers,
  budgetHelpers,
  budgetSpendingData,
  budgetSpendingKeys,
  tableHelpers
} from '../../../../utils/helpers';
import {colorsStyles} from '../../../../utils/styles';
import {ReactComponent as EmptySvgImage} from '../../../../static/images/pages/budgets/empty-budgets.svg';
import {alertActions} from '../../../../state/actions/alert.actions';
import {budgetActions} from '../../../../state/actions/budget.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {budgetStatusConstants, invoiceApprovalStatusConstants} from '../../../../constants';

const tableDataState = { loading: false, data: [] };

const BudgetActiveInfo = ({
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const [invoices, setInvoices] = useState(tableDataState);

  const budget = useSelector(state => state?.budget?.budgetRequest);

  const dispatch = useDispatch();

  useEffect(() => {
    const budgetId = budget?.id;
    if (!budgetId) return;
    loadInvoices({budgetId});
  }, [budget?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInvoices = ({budgetId = budget?.id, initial = true, query} = {}) => {
    if (!initial) setInvoices(prevState => ({ ...prevState, loading: true }));
    dispatch(invoicesActions.getInvoices({
      query: {
        budget_request_id: budgetId,
        approval_status: [invoiceApprovalStatusConstants.PENDING, invoiceApprovalStatusConstants.APPROVED],
        ...query
      },
      successCallback: (response) => {
        setInvoices(prevState => ({
          ...prevState,
          loading: false,
          data: initial ? response.invoices : [...prevState.data, ...response.invoices],
          pagination: response.pagination,
        }));
      },
      errorCallback: () => setInvoices({loading: false, data: []})
    }))
  }

  const isEmptySpending = useMemo(() => {
    const spent = budget?.spent_amount || 0;
    const invoiced = budget?.invoiced_amount || 0;
    return spent === 0 && invoiced === 0;
  }, [budget]);

  // method to calculate utilization percentage if it's zero but there are spent or invoiced amounts
  const utilizationPercentage = useMemo(() => {
    const percentage = budget?.utilization_percentage || 0;
    if (percentage > 0) return percentage;

    const spent = budget?.spent_amount || 0;
    const invoiced = budget?.invoiced_amount || 0;
    if (percentage === 0 && (spent > 0 || invoiced > 0)) {
      const totalSpent = spent + invoiced;
      const budgetLimit = budget?.limit_amount || 0;
      if (budgetLimit === 0) return 0;
      return (totalSpent / budgetLimit) * 100;
    }
    return percentage;
  }, [budget]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOverBudget = useMemo(() => {
    return budget?.status === budgetStatusConstants.OVER_BUDGET || utilizationPercentage > 100;
  }, [budget?.status, utilizationPercentage]);

  const budgetLimitAmount = useMemo(() => budget?.limit_amount || 0, [budget?.limit_amount]);

  const { remainingBudget, progressProps } = useMemo(() => {
    const spent = budget?.spent_amount || 0;
    const invoiced = budget?.invoiced_amount || 0;
    const totalSpent = spent + invoiced;
    const budgetLimit = budget?.limit_amount || 0;
    return budgetHelpers.calculateBudgetProgress({
      clearUtilization: true,
      totalSpent,
      budgetLimit,
      spent,
    });
  }, [budget]);

  const budgetName = budgetHelpers.getBudgetName(budget);

  const renderTitle = ({key, direction = 'horizontal'}) => {
    const data = budgetSpendingData[key];
    if (!data) return null;
    const amount = budget?.[data.amountKey] || 0;
    if (amount === 0) return null;

    return (
      <Space size='small' direction={direction}>
        <Space size={4}>
          <StyledBudgetActiveInfoPanelDot color={data?.color} />
          <StyledBudgetActiveInfoPanelTitle>{t(data?.labelKey)}</StyledBudgetActiveInfoPanelTitle>
          <InfoCircleOutlined style={{color: colorsStyles.neutral_7, fontSize: 14}} />
        </Space>
        <StyledBudgetActiveInfoPanelAmount>
          {amountHelpers.getAmountWithCurrencyCode(budget?.[data.amountKey] || 0)}
        </StyledBudgetActiveInfoPanelAmount>
      </Space>
    )
  }

  const loadBudgetData = () => {
    const budgetId = budget?.id;
    if (!budgetId) return;
    dispatch(budgetActions.getBudgetRequest({id: budgetId}));
  }

  const handleUnlink = (invoice) => {
    setInvoices({...invoices, loading: true});
    dispatch(invoicesActions.cancelPaymentRequest({
      id: invoice.id,
      successCallback: () => {
        setInvoices({...invoices, loading: false, data: invoices.data.filter(item => item.id !== invoice.id) });
        loadBudgetData();
      },
      errorCallback: () => {
        setInvoices({...invoices, loading: false});
        alertActions.error(t('budgets:alerts.unassignBudgetFromInvoiceError'));
      }
    }));
  }

  const invoicesLoadMoreProps = tableHelpers.getLoadMoreProps({
    tableData: invoices, loadData: ({query}) => loadInvoices({initial: false, query})
  });

  return (
    <StyledBudgetActiveInfo size='large' direction='vertical' {...rest}>
      <StyledBudgetActiveInfoPanel size={32} direction='vertical'>
        <StyledBudgetActiveInfoPanelHeader>
          <StyledBudgetActiveInfoPanelHeaderTitle>{budgetName}</StyledBudgetActiveInfoPanelHeaderTitle>
          {Boolean(budgetLimitAmount) && (
            <Space size={40} wrap>
              <Space size={4} direction='vertical'>
                <StyledBudgetActiveInfoPanelTitle>{t('Budget')}</StyledBudgetActiveInfoPanelTitle>
                <StyledBudgetActiveInfoPanelAmount>
                  {amountHelpers.getAmountWithCurrencyCode(budgetLimitAmount)}
                </StyledBudgetActiveInfoPanelAmount>
              </Space>
              <Space size={4} direction='vertical' className={remainingBudget.type}>
                <StyledBudgetActiveInfoPanelTitle>{t('remaining')} {t('Budget')}</StyledBudgetActiveInfoPanelTitle>
                <Space size='small'>
                  <StyledBudgetActiveInfoPanelAmount>
                    {amountHelpers.getAmountWithCurrencyCode(remainingBudget.amount)}
                  </StyledBudgetActiveInfoPanelAmount>
                  <StyledBudgetActiveInfoPanelPercentage type={remainingBudget.type}>
                    {remainingBudget.percentage.toFixed(2)}%
                  </StyledBudgetActiveInfoPanelPercentage>
                </Space>
              </Space>
            </Space>
          )}
        </StyledBudgetActiveInfoPanelHeader>
        <OverflowProgress
          {...progressProps}
          percent={isOverBudget ? utilizationPercentage : remainingBudget.percentage}
          hidePercent={true}
          strokeColor={budgetSpendingData[budgetSpendingKeys.INVOICED].color}
          size={{ height: 15 }}
          showInfo={false}
          trailColor={colorsStyles.neutral_3}
          strokeLinecap='butt'
        />
        <Space size={40} wrap>
          {renderTitle({key: budgetSpendingKeys.SPENT})}
          {renderTitle({key: budgetSpendingKeys.INVOICED})}
        </Space>
      </StyledBudgetActiveInfoPanel>

      {Boolean(invoices.data.length) && (
        <StyledBudgetActiveInfoPanel direction='vertical' size={32}>
          {renderTitle({key: budgetSpendingKeys.INVOICED, direction: 'vertical'})}
          <BudgetInvoicesTable
            data={invoices.data}
            loading={invoices.loading}
            onUnlink={handleUnlink}
          />
          <LoadMoreButton
            {...invoicesLoadMoreProps}
            loading={invoices.loading}
          >
            {t('showMore')}
          </LoadMoreButton>
        </StyledBudgetActiveInfoPanel>
      )}

      {/*{!isEmptySpending && (*/}
      {/*  <>*/}
      {/*    <StyledBudgetActiveInfoPanel direction='vertical' size={32}>*/}
      {/*      {renderTitle({key: budgetSpendingKeys.SPENT, direction: 'vertical'})}*/}
      {/*      <BudgetTransactionsTable />*/}
      {/*    </StyledBudgetActiveInfoPanel>*/}
      {/*  </>*/}
      {/*)}*/}

      {isEmptySpending && (
        <StyledBudgetActiveInfoPanel>
          <StyledBudgetActiveInfoEmpty
            description={t('budgets:emptyBudgetSpending.description')}
            image={<EmptySvgImage />}
            title={t('budgets:emptyBudgetSpending.title')}
          />
        </StyledBudgetActiveInfoPanel>
      )}
    </StyledBudgetActiveInfo>
  );
}

export default BudgetActiveInfo;
